import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

/**
 * Chapter marker for episode navigation
 */
export interface PlayableChapter {
  id: string;
  title: string;
  startTimeMs: number;
  endTimeMs: number;
  description?: string | null;
}

/**
 * Playable episode data - compatible with both full Episode from GraphQL
 * and EpisodeSummary from discovery metadata (when audio_s3_key is added)
 */
export interface PlayableEpisode {
  episodeId: string;
  topicSlug: string;
  topicName: string;
  title: string;
  summary: string;
  audioS3Key: string;
  durationSeconds: number;
  publishedAt: string;
  chapters?: PlayableChapter[];
}

/**
 * Episode in the queue with completion status for autoplay logic
 */
export interface QueuedEpisode extends PlayableEpisode {
  isCompleted: boolean;
}

interface PlayerContextValue {
  currentEpisode: PlayableEpisode | null;
  isPlaying: boolean;
  autoplayEnabled: boolean;
  episodeQueue: QueuedEpisode[];
  playEpisode: (episode: PlayableEpisode) => void;
  closePlayer: () => void;
  setIsPlaying: (playing: boolean) => void;
  setAutoplayEnabled: (enabled: boolean) => void;
  setEpisodeQueue: (queue: QueuedEpisode[]) => void;
  playNextEpisode: () => boolean; // Returns true if next episode was found and started
  onEpisodeEnded: () => void; // Called when current episode finishes
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

const AUTOPLAY_STORAGE_KEY = 'autoplayEnabled';

export function PlayerProvider({ children }: { children: ReactNode }) {
  const [currentEpisode, setCurrentEpisode] = useState<PlayableEpisode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [episodeQueue, setEpisodeQueue] = useState<QueuedEpisode[]>([]);
  const [autoplayEnabled, setAutoplayEnabledState] = useState(() => {
    const saved = localStorage.getItem(AUTOPLAY_STORAGE_KEY);
    return saved === 'true';
  });

  const setAutoplayEnabled = useCallback((enabled: boolean) => {
    setAutoplayEnabledState(enabled);
    localStorage.setItem(AUTOPLAY_STORAGE_KEY, String(enabled));
  }, []);

  const playEpisode = useCallback((episode: PlayableEpisode) => {
    setCurrentEpisode(episode);
    setIsPlaying(true); // Auto-play when selecting a new episode
  }, []);

  const closePlayer = useCallback(() => {
    setCurrentEpisode(null);
    setIsPlaying(false);
  }, []);

  // Find and play the next uncompleted episode in the queue
  const playNextEpisode = useCallback((): boolean => {
    if (!currentEpisode || episodeQueue.length === 0) {
      return false;
    }

    // Find the current episode's index in the queue
    const currentIndex = episodeQueue.findIndex(
      (ep) => ep.episodeId === currentEpisode.episodeId
    );

    if (currentIndex === -1 || currentIndex >= episodeQueue.length - 1) {
      // Current episode not in queue or is the last one
      return false;
    }

    // Look for the next uncompleted episode starting from the next position
    for (let i = currentIndex + 1; i < episodeQueue.length; i++) {
      const nextEpisode = episodeQueue[i];
      if (!nextEpisode.isCompleted) {
        playEpisode(nextEpisode);
        return true;
      }
    }

    // All remaining episodes are completed
    return false;
  }, [currentEpisode, episodeQueue, playEpisode]);

  // Called when the current episode ends
  const onEpisodeEnded = useCallback(() => {
    setIsPlaying(false);
    if (autoplayEnabled) {
      playNextEpisode();
    }
  }, [autoplayEnabled, playNextEpisode]);

  return (
    <PlayerContext.Provider
      value={{
        currentEpisode,
        isPlaying,
        autoplayEnabled,
        episodeQueue,
        playEpisode,
        closePlayer,
        setIsPlaying,
        setAutoplayEnabled,
        setEpisodeQueue,
        playNextEpisode,
        onEpisodeEnded,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}
